// @flow
import React from 'react';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { DoctorForm as DoctorFormView } from '@views';
import { clinicActions } from '@context/clinics';
import { fetchTeam, selectTeam, selectTeamMember } from '@context/team';
import { router } from '@router';
import { mapBundledPermission, canEditDoctor, mapManagesClinic } from '@helpers/connect';
import { UserNotifications } from '@helpers/helpers';
import type { User, DoctorsState, Room, BundledPermissions } from '@types';
import { logout } from '@context/user';

type Props = {
  user: User,
  dispatch: Function,
  doctors: DoctorsState,
  rooms: Array<Room>,
  id: number,
  permissions: BundledPermissions,
  isSaving: boolean,
  isDeleting: boolean,
  isManager: boolean,
  canEdit: boolean,
  // The professional as Equipe knows them (null until the team loads, and for whoever isn't a manager)
  member: ?Object,
  // Equipe's answer on the plan's limit, fresher than the user's planInclude once loaded
  canInviteDoctor: ?boolean,
  teamLoaded: boolean,
};

// The Painel's default for a new professional
const NEW_DOCTOR = { defaultAppointmentDuration: 60 };

class DoctorForm extends React.Component<Props> {
  componentDidMount() {
    const { dispatch, isManager, teamLoaded, id } = this.props;
    if (isManager && !teamLoaded) dispatch(fetchTeam());
    // A member invited elsewhere is in Equipe before the clinic's data has them
    if (id !== 'novo' && isEmpty(this.formData())) dispatch(clinicActions.getClinics());
  }


  render() {
    const {
      user, id, rooms, permissions, isSaving, isDeleting, isManager, member, canInviteDoctor,
    } = this.props;

    const isEdit = id !== 'novo';
    const isOwner = Boolean(member && member.owner);
    // Removing or demoting oneself here would leave the app on a clinic it can't use
    const isSelf = Boolean(member && member.userId === get(user, 'id'));
    // The owner is always a manager: locked until Equipe says who owns the clinic
    const managerLocked = isEdit && (!member || isOwner || isSelf);
    const formData = isEdit ? this.formData() : NEW_DOCTOR;

    return (
      <DoctorFormView
        key={isEmpty(formData) ? 'loading' : 'loaded'}
        user={user}
        formData={formData}
        onCancel={this.handleCancel}
        onSave={this.handleSave}
        onRemove={isEdit && isManager && member && !isOwner && !isSelf ? this.handleRemove : null}
        id={id}
        rooms={rooms}
        isAllowed={isEdit ? this.props.canEdit : isManager}
        isFeatureEnabled={isEdit || (canInviteDoctor == null ? permissions.planInclude.inviteDoctors : canInviteDoctor)}
        isManager={isManager}
        isOwner={isOwner}
        isSelf={isSelf}
        managerLocked={managerLocked}
        onLogout={this.handleLogout}
        permissions={this.props.permissions}
        isSaving={isSaving || isDeleting}
      />
    );
  }

  formData = () => get(this.props.doctors, ['data', String(this.props.id)], {});

  handleLogout = () => this.props.dispatch(logout());

  backToList = () => {
    this.props.dispatch(this.props.isManager ? router.pushPath('team') : router.pushPath('settingsList'));
  };

  handleCancel = () => this.backToList();

  handleSave = (data: Object) => {
    const { dispatch, id } = this.props;
    if (id !== 'novo') {
      dispatch(clinicActions.updateDoctor(id, data, (ok) => {
        if (ok) this.backToList();
      }));
    } else {
      dispatch(clinicActions.createDoctor(data, (ok) => {
        if (ok) this.backToList();
      }));
    }
  };

  handleRemove = async () => {
    if (!await UserNotifications.confirmDelete('alert/team/remove_doctor')) return;
    const { dispatch, id } = this.props;
    // Equipe loads the clinic again on opening
    dispatch(clinicActions.deleteDoctor(id, (ok) => {
      if (ok) this.backToList();
    }));
  };
}

const mapStateToProps = (state, ownProps) => {
  const id = get(ownProps, ['match', 'params', 'id']);
  return {
    user: state.context.user,
    doctors: get(state, ['context', 'doctors'], {}),
    isSaving: get(state, ['context', 'doctors', 'isSaving'], false),
    isDeleting: get(state, ['context', 'doctors', 'isDeleting'], false),
    rooms: get(state, ['context', 'clinics', 'activeClinic', 'rooms'], []),
    id,
    permissions: mapBundledPermission(state),
    isManager: mapManagesClinic(state),
    canEdit: canEditDoctor(state, id),
    member: selectTeamMember(state, 'Doctor', id),
    canInviteDoctor: get(selectTeam(state), 'canInviteDoctor', null),
    teamLoaded: Boolean(selectTeam(state)),
  };
};

export default connect<any, any, any, any, any, any>(
  mapStateToProps,
)(DoctorForm);
